import Link from 'next/link';

//hooks
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useUser from '@/utils/hooks/swr/useUser';
import useUserMarketing from '@/utils/hooks/swr/useUserMarketing';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import useMarketingUrl from '@/utils/urls/useMarketingUrl';
import { hasCSMPlan } from '@/components/pages/settings/subscription-invoices/PlanModal/Components/PlanCard/utils';

import { FaqCTA } from '@/components/pages/settings/subscription-invoices/PageContent/Components/FaqCTA';

//components
import Button from '@/components/base/Button';
import H3 from '@/components/base/Headings/H3';
import { CurrentUserPlan } from '@/components/pages/settings/subscription-invoices/CurrentUserPlan';
import { PastDueButton } from '@/components/pages/settings/subscription-invoices/PageContent/Components/PastDueButton';
import { RightSection } from '@/components/pages/settings/subscription-invoices/PageContent/Components/RightSection';
import { Trans } from '@/components/tools/translation';
import SettingsWrapper from '@/components/layouts/LayoutPlatform/components/SettingsWrapper';

type ChangeYourPlanActionProps = {
    togglePlanModal: SubscriptionPageProps['togglePlanModal'];
};
function ChangeYourPlanAction({ togglePlanModal }: ChangeYourPlanActionProps) {
    const { subscription } = useSubscription();
    const { user } = useUser();
    const { t } = useTranslation('settings');
    const { marketingUrl } = useMarketingUrl();
    const { userMarket } = useUserMarketing();

    if (user.past_due) {
        return (
            <div className="mt-10 flex flex-col items-center space-y-3 lg:items-end">
                <PastDueButton />
                <FaqCTA data-testid="change-plan-faq" />
            </div>
        );
    }

    const hasCSM = hasCSMPlan(subscription, userMarket);

    if (!hasCSM) {
        return (
            <div className="mt-10 flex flex-col items-center space-y-3 lg:items-end">
                <Button
                    onClick={togglePlanModal}
                    size="xl"
                    data-testid="button-change-plan"
                    className="w-full lg:w-max">
                    {t('subscription_invoices_page.change_your_plan')}
                </Button>
                <FaqCTA data-testid="change-plan-faq" />
            </div>
        );
    }

    return (
        <div className="mt-10 flex flex-col items-end space-y-3">
            <Button
                size="xl"
                as={Link}
                href={marketingUrl('contact')}
                target="_blank"
                rel="help noreferrer noopener"
                data-testid="button-contact-us">
                {t('commons:get_in_touch_with_us')}
            </Button>
            <span className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                <Trans
                    i18nKey="settings:subscription_invoices_page.new_pricing.check_our_plan"
                    components={{
                        tag0: (
                            <button
                                data-testid="button-check-our-plan"
                                onClick={togglePlanModal}
                                className="font-GSemiBold text-icoBlue underline focus:ring-0 focus:ring-offset-0"
                            />
                        )
                    }}
                />
            </span>
        </div>
    );
}

type SubscriptionPageProps = {
    togglePlanModal: () => void;
};
export function SubscriptionPage({ togglePlanModal }: SubscriptionPageProps) {
    const { t } = useTranslation('settings');

    return (
        <>
            <SettingsWrapper.MainContent>
                <H3 className="mb-5">{t('subscription_invoices_page.your_current_plan')}</H3>
                <CurrentUserPlan />
                <ChangeYourPlanAction togglePlanModal={togglePlanModal} />
            </SettingsWrapper.MainContent>
            <RightSection togglePlanModal={togglePlanModal} />
        </>
    );
}
